import React from 'react';
import { cn } from '@/lib/utils';

// 基础 Skeleton 组件
interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('animate-pulse rounded-md bg-[rgba(0,0,0,0.06)]', className)}
      {...props}
    />
  );
});
Skeleton.displayName = 'Skeleton';

// 按钮骨架屏
interface SkeletonButtonProps extends SkeletonProps {
  size?: 'sm' | 'md' | 'lg';
  width?: string | number;
}

const SkeletonButton = React.forwardRef<HTMLDivElement, SkeletonButtonProps>(
  ({ className, size = 'md', width, ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-6',
      md: 'h-8',
      lg: 'h-12'
    };

    return (
      <Skeleton
        ref={ref}
        className={cn('rounded', sizeClasses[size], className)}
        style={{ width: width || 'auto' }}
        {...props}
      />
    );
  }
);
SkeletonButton.displayName = 'SkeletonButton';

// 卡片骨架屏
interface SkeletonCardProps extends SkeletonProps {
  showHeader?: boolean;
  showContent?: boolean;
  showFooter?: boolean;
  headerHeight?: string;
  contentHeight?: string;
  footerHeight?: string;
}

const SkeletonCard = React.forwardRef<HTMLDivElement, SkeletonCardProps>(
  (
    {
      className,
      showHeader = true,
      showContent = true,
      showFooter = false,
      headerHeight = 'h-6',
      contentHeight = 'h-20',
      footerHeight = 'h-4',
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn('rounded-lg border border-[#F0F0F0] bg-white p-3', className)}
        {...props}
      >
        {showHeader && (
          <div className="mb-2 flex items-center justify-between">
            <Skeleton className={cn('w-48', headerHeight)} />
            <Skeleton className={cn('w-10', headerHeight)} />
          </div>
        )}
        {showContent && (
          <div className="mb-2 space-y-2">
            <div className="flex gap-3">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-10" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        )}
        {showFooter && <Skeleton className={cn('w-full', footerHeight)} />}
      </div>
    );
  }
);
SkeletonCard.displayName = 'SkeletonCard';

// 表格骨架屏
interface SkeletonTableProps extends SkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

const SkeletonTable = React.forwardRef<HTMLDivElement, SkeletonTableProps>(
  ({ className, rows = 3, columns = 4, showHeader = true, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {showHeader && (
          <div className="flex gap-3">
            {Array.from({ length: columns }).map((_, index) => (
              <Skeleton key={`header-${index}`} className="h-4 w-10" />
            ))}
          </div>
        )}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex gap-3">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-3 w-10" />
            ))}
          </div>
        ))}
      </div>
    );
  }
);
SkeletonTable.displayName = 'SkeletonTable';

// 文本骨架屏
interface SkeletonTextProps extends SkeletonProps {
  lines?: number;
  lastLineWidth?: string;
}

const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ className, lines = 3, lastLineWidth = '75%', ...props }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton key={index} className={cn('h-4', index === lines - 1 ? 'w-[75%]' : 'w-full')} />
        ))}
      </div>
    );
  }
);
SkeletonText.displayName = 'SkeletonText';

// 头像骨架屏
interface SkeletonAvatarProps extends SkeletonProps {
  size?: 'sm' | 'md' | 'lg';
}

const SkeletonAvatar = React.forwardRef<HTMLDivElement, SkeletonAvatarProps>(
  ({ className, size = 'md', ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-6 w-6',
      md: 'h-8 w-8',
      lg: 'h-12 w-12'
    };

    return (
      <Skeleton ref={ref} className={cn('rounded-full', sizeClasses[size], className)} {...props} />
    );
  }
);
SkeletonAvatar.displayName = 'SkeletonAvatar';

// 复合骨架屏 - 根据 Figma 设计
const SkeletonDashboard = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-3 px-4', className)} {...props}>
        {/* 顶部渐变卡片 */}
        <div className="bg-white p-3">
          <div className="rounded-lg border border-[#F0F0F0] p-3">
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-7 w-full" />
            </div>
          </div>
        </div>

        {/* 主内容区域 */}
        <div className="space-y-3 rounded-lg bg-white p-3">
          {/* 标签栏 */}
          <div className="flex gap-3">
            <Skeleton className="h-5 w-10" />
            <Skeleton className="h-5 w-10" />
            <Skeleton className="h-5 w-10" />
            <Skeleton className="h-5 w-10" />
          </div>

          {/* 卡片组 */}
          <div className="space-y-3">
            {/* 卡片 1 */}
            <div className="rounded-md border border-[#F0F0F0] p-3">
              <div className="mb-2 flex items-center justify-between">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-6 w-10" />
              </div>
              <div className="mb-2 flex gap-3">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>

            {/* 卡片 2 */}
            <div className="rounded-md border border-[#F0F0F0] p-3">
              <div className="mb-2 flex items-center justify-between">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-6 w-10" />
              </div>
              <div className="mb-2 flex gap-3">
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-10" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>

            {/* 卡片 3 */}
            <div className="rounded-md border border-[#F0F0F0] p-3">
              <div className="mb-2">
                <Skeleton className="h-6 w-32" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
SkeletonDashboard.displayName = 'SkeletonDashboard';

export {
  Skeleton,
  SkeletonButton,
  SkeletonCard,
  SkeletonTable,
  SkeletonText,
  SkeletonAvatar,
  SkeletonDashboard
};
